import { motion } from 'framer-motion';

import { useModal } from '../../hooks/modalContext';

import { useEffect, useState } from 'react';
import { usePoints } from '../../hooks/pointsContext';
import { convertAmountsToPoints, redeemGiftCardRequest } from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { showToast } from '../../utils/showToast';

import { Spinner } from '../../UI/Spinner/Spinner';
import { Icon } from '../../Icons/Icon';
import { ChevronLeft } from 'lucide-react';

const DEFAULT_REDEEM_ERROR_MESSAGE = 'Failed to redeem gift card. Please try again later.';

const REDEEM_ERROR_MESSAGES: Record<string, string> = {
  no_successful_payment_found:
    'You need at least one successful rent payment before you can redeem points for a gift card.',
  tenant_identity_not_verified: 'Verify your identity before redeeming points for a gift card.',
  email_not_verified: 'Verify your email address before redeeming points for a gift card.',
};

const RedeemGiftCard = () => {
  const { setModal, modalState } = useModal();

  const giftPrices = modalState?.giftPrices || [];
  const brandCode = modalState?.brandCode || '';
  const from = modalState?.fromModal || '';
  const { auth } = useAuth();
  const { points, refreshPoints } = usePoints();
  const [amountRedeem, setAmountRedeem] = useState<number>(0);

  const [giftPointsMap, setGiftPointsMap] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(false);
  const [loadingMail, setLoadingMail] = useState(false);

  // useEffect(() => {
  //   const fetchGiftCardPoints = async () => {
  //     const map: Record<number, number> = {};
  //     for (const price of giftPrices) {
  //       setLoading(true);
  //       try {
  //         const res = await convertAmountsToPoints(auth?.data?.token, price, 'cents');
  //         setLoading(false);
  //         map[price] = Number(res.points); // ensure points is a number
  //       } catch (error) {
  //         setLoading(false);
  //         console.error(`Failed to convert ${price} to points`, error);
  //       } finally {
  //         setLoading(false);
  //       }
  //     }
  //     setGiftPointsMap(map);
  //   };

  //   if (auth?.data.token && giftPrices.length > 0) {
  //     fetchGiftCardPoints();
  //   }
  // }, [giftPrices, auth?.data.token]);

  useEffect(() => {
    const fetchGiftCardPoints = async () => {
      if (!auth?.data.token || giftPrices.length === 0) return;

      const amountsToSend = giftPrices;
      setLoading(true);

      try {
        const res = await convertAmountsToPoints(auth.data.token, amountsToSend, 'cents');

        const newMap: Record<number, number> = {};
        const convertedPoints = res.data.points || [];

        amountsToSend.forEach((price, index) => {
          const pointValue = convertedPoints[index];

          if (pointValue !== undefined && !isNaN(Number(pointValue))) {
            newMap[price] = Number(pointValue);
          }
        });

        setGiftPointsMap(newMap);
      } catch (error) {
        console.error(`Failed to convert prices to points`, error);
        setGiftPointsMap({});
      } finally {
        setLoading(false);
      }
    };

    fetchGiftCardPoints();
  }, [giftPrices, auth?.data.token]);

  const sendRedeemGiftCardRequest = async () => {
    try {
      setLoadingMail(true);
      const response = await redeemGiftCardRequest(
        auth?.data.token || '',
        brandCode,
        amountRedeem / 100
      );
      if (response.message === 'success') {
        showToast({
          message: `Gift card redeemed successfully! Gift card sent to your registered email address.`,
          type: 'success',
        });
        setTimeout(async () => {
          await refreshPoints();
        }, 1000);
      } else {
        showToast({
          message: 'Failed to redeem gift card. Please try again later.',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error redeeming gift card:', error);
      const errorCode = error instanceof Error ? error.message : '';
      showToast({
        message: REDEEM_ERROR_MESSAGES[errorCode] ?? DEFAULT_REDEEM_ERROR_MESSAGE,
        type: 'error',
      });
    } finally {
      setModal(null);
      setLoadingMail(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <motion.div
      className='relative shadow-xl w-[433px] max-h-auto bg-[linear-gradient(229deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] rounded-[12px] flex flex-col overflow-auto border border-[#E9EDF9]'
      initial='hidden'
      animate='visible'
      exit='exit'
    >
      <div className='flex flex-col gap-9 items-center justify-center px-10 py-8'>
        {points && (
          <div className='w-full '>
            <div className='flex justify-between items-center w-full'>
              <ChevronLeft
                className='text-main cursor-pointer'
                onClick={() => {
                  setModal(null);
                  setModal(from, {
                    brandCode: brandCode,
                  });
                }}
              />

              <div className='flex items-center gap-2 bg-[var(--Primary-Color,#0055FF)] rounded-full px-3 py-2 h-9 cursor-pointer'>
                <Icon
                  name='wallet'
                  color='rgba(236,188,33,1)'
                  className='w-[22px] h-[22px]'
                  strokeWidth={0.5}
                />
                <span className='text-white text-lg'>
                  {Number(points?.data?.points?.totalAvailable ?? 0).toLocaleString('en-US')}
                </span>
              </div>
            </div>
          </div>
        )}
        <span className='text-main text-base'>Use your TenantPay points to grab gift cards!</span>

        {giftPrices && giftPrices.length > 0 ? (
          <div className='grid grid-cols-2 max-[370px]:grid-cols-1 gap-4 w-full'>
            {giftPrices.map((price) => {
              const isSelected = amountRedeem === price;

              // const isEligible="True"
              const isEligible =
                Number(giftPointsMap[price]) <= Number(points?.data?.points?.totalAvailable ?? 0);
              return (
                <div
                  key={price}
                  onClick={() => {
                    if (isEligible) setAmountRedeem(price);
                  }}
                  className={`group flex flex-col px-4 py-4 justify-center items-center gap-2 rounded-[8px] border transition-colors
                        ${isEligible ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}
                        ${
                          isSelected
                            ? 'border-[rgba(138,146,171,0.30)] bg-[#F2F4F9]'
                            : isEligible
                              ? 'border-[#F7F8FA] bg-[rgba(236,238,243,0.40)] hover:border-[rgba(138,146,171,0.30)] hover:bg-[#F2F4F9]'
                              : 'border-[#F7F8FA] bg-[rgba(236,238,243,0.40)]'
                        }
                      `}
                >
                  <span
                    className={`text-base ${
                      isSelected
                        ? 'text-main'
                        : `${
                            isEligible ? 'text-[#8A92AB] group-hover:text-main' : 'text-[#8A92AB]'
                          }`
                    }`}
                  >
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(price / 100)}
                  </span>
                  <span
                    className={`text-base font-semibold ${
                      isSelected
                        ? 'text-main'
                        : `${
                            isEligible ? 'text-[#8A92AB] group-hover:text-main' : 'text-[#8A92AB]'
                          }`
                    }`}
                  >
                    {giftPointsMap[price] != null
                      ? `${giftPointsMap[price].toLocaleString()} pts`
                      : '...'}{' '}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className='text-light/70'>No price options available at the moment.</p>
        )}

        {amountRedeem ? (
          <div className='flex justify-between w-full'>
            <span className='text-main text-base'>Redeem total</span>
            <span className='text-main font-semibold'>
              {new Intl.NumberFormat('en-US').format(giftPointsMap[amountRedeem] || 0)} pts
            </span>
          </div>
        ) : null}

        <div className='flex gap-5 w-full'>
          <button
            className='cancelBtn'
            onClick={() => {
              setModal(null);
              setModal(from, {
                brandCode: brandCode,
              });
            }}
          >
            Cancel
          </button>
          <button
            className='submitBtn'
            onClick={() => {
              sendRedeemGiftCardRequest();
            }}
            disabled={!amountRedeem || loadingMail}
          >
            Redeem
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export { RedeemGiftCard };
